import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import type { Case } from "@/lib/useRealtimeCases";
import { useAuth } from "@/lib/AuthContext";
import { useCaseTasks } from "@/lib/CaseTasksContext";
import { useTeam } from "@/lib/useTeam";
import { CASE_TASK_LABELS, CASE_TASK_NOTE_MAX_LENGTH, CASE_TASK_TYPES, type CaseTaskType } from "@/lib/caseTasks";
import { clampMenuPosition, firstNameOf, teamRecipients } from "@/lib/kanbanTasks";
import "@/components/tasks/tasks.css";

export interface CaseContextMenuProps {
  caseData: Case;
  /** Position du curseur (clientX / clientY). */
  x: number;
  y: number;
  onClose: () => void;
  onNotify: (message: string, type: "success" | "error") => void;
  /** Mise à jour immédiate de la carte (le temps que le temps réel confirme). */
  onPriorityChange?: (caseId: string, priority: boolean | null) => void;
}

type Step = "root" | "types" | "form";

const TASK_ICONS: Record<CaseTaskType, string> = {
  qualification_call: "M3 5.5A2.5 2.5 0 0 1 5.5 3h1.1c.5 0 .9.3 1 .8l.8 3.2c.1.4 0 .9-.4 1.1l-1.4 1a11 11 0 0 0 5.3 5.3l1-1.4c.3-.4.7-.5 1.1-.4l3.2.8c.5.1.8.5.8 1v1.1A2.5 2.5 0 0 1 16.5 21h-1C8.6 21 3 15.4 3 8.5v-3Z",
  phone_followup: "M3 5.5A2.5 2.5 0 0 1 5.5 3h1.1c.5 0 .9.3 1 .8l.8 3.2c.1.4 0 .9-.4 1.1l-1.4 1a11 11 0 0 0 5.3 5.3l1-1.4c.3-.4.7-.5 1.1-.4l3.2.8c.5.1.8.5.8 1v1.1A2.5 2.5 0 0 1 16.5 21h-1C8.6 21 3 15.4 3 8.5v-3ZM15 3a6 6 0 0 1 6 6M15 6.5A2.5 2.5 0 0 1 17.5 9",
  send_quote: "M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7 0v5h5M9 13h6M9 17h4",
  send_deposit_invoice: "M6 3h12v18l-3-2-3 2-3-2-3 2V3Zm3 6h6M9 13h6",
  send_final_invoice: "M6 3h12v18l-3-2-3 2-3-2-3 2V3Zm3.5 8.5 2 2 3.5-4",
};

function Icon({ d, className = "h-4 w-4" }: { d: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

const ITEM =
  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-gray-800 outline-none hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:ring-2 focus-visible:ring-indigo-500";

export function CaseContextMenu({ caseData, x, y, onClose, onNotify, onPriorityChange }: CaseContextMenuProps) {
  const { user } = useAuth();
  const { createTask, setCasePriority } = useCaseTasks();
  const { membersOfCenter } = useTeam();
  const menuRef = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const [step, setStep] = useState<Step>("root");
  const [taskType, setTaskType] = useState<CaseTaskType | null>(null);
  const [target, setTarget] = useState<"team" | "self">("team");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const formId = useId();

  const center = caseData.center?.trim() || null;
  const recipients = useMemo(
    () => (center ? teamRecipients(membersOfCenter(center), user?.id) : []),
    [center, membersOfCenter, user?.id],
  );
  const teamPossible = !!center && recipients.length > 0;
  const company = caseData.company_name || [caseData.client_first_name, caseData.client_last_name].filter(Boolean).join(" ") || "ce dossier";

  useEffect(() => {
    if (!teamPossible) setTarget("self");
  }, [teamPossible]);

  // Mémorise le focus d'origine, le rend à la fermeture.
  useEffect(() => {
    restoreFocus.current = document.activeElement as HTMLElement | null;
    return () => restoreFocus.current?.focus?.({ preventScroll: true });
  }, []);

  // Position : mesurée puis gardée dans la fenêtre (à chaque changement d'étape, la taille change).
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition(clampMenuPosition(x, y, rect.width, rect.height, window.innerWidth, window.innerHeight));
  }, [x, y, step, teamPossible]);

  // Focus : premier élément de l'étape.
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const first = el.querySelector<HTMLElement>(step === "form" ? "input:not(:disabled), textarea, button[type=submit]" : '[role="menuitem"]');
    first?.focus({ preventScroll: true });
  }, [step]);

  // Clic extérieur, défilement, redimensionnement : fermeture.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) onClose();
    };
    const onScroll = (event: Event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    window.addEventListener("blur", onClose);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("blur", onClose);
    };
  }, [onClose]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (step === "form") return;
      const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? []);
      const index = items.indexOf(document.activeElement as HTMLElement);
      const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus({ preventScroll: true });
      if (event.key === "ArrowDown") { event.preventDefault(); focusAt(index + 1); }
      else if (event.key === "ArrowUp") { event.preventDefault(); focusAt(index < 0 ? -1 : index - 1); }
      else if (event.key === "Home") { event.preventDefault(); focusAt(0); }
      else if (event.key === "End") { event.preventDefault(); focusAt(-1); }
      else if (event.key === "ArrowLeft" && step === "types") { event.preventDefault(); setStep("root"); }
      else if (event.key === "ArrowRight" && step === "root" && (document.activeElement as HTMLElement | null)?.dataset.submenu) {
        event.preventDefault();
        setStep("types");
      } else if (event.key === "Tab") {
        event.preventDefault();
        focusAt(event.shiftKey ? index - 1 : index + 1);
      }
    },
    [onClose, step],
  );

  const togglePriority = async () => {
    const next = !caseData.is_priority;
    setBusy(true);
    onPriorityChange?.(caseData.id, next);
    try {
      await setCasePriority(caseData.id, next);
      onNotify(next ? `« ${company} » est marqué prioritaire.` : `Priorité retirée pour « ${company} ».`, "success");
      onClose();
    } catch (err) {
      onPriorityChange?.(caseData.id, null);
      onNotify(err instanceof Error ? err.message : "Une erreur est survenue. Réessayez.", "error");
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!taskType || busy) return;
    setBusy(true);
    try {
      await createTask({ caseId: caseData.id, taskType, note, assignToSelf: target === "self" });
      const who = target === "self" ? "vous" : `l'équipe ${center}`;
      onNotify(`Tâche « ${CASE_TASK_LABELS[taskType]} » assignée à ${who}.`, "success");
      onClose();
    } catch (err) {
      onNotify(err instanceof Error ? err.message : "Une erreur est survenue. Réessayez.", "error");
      setBusy(false);
    }
  };

  const back = (to: Step) => (
    <button type="button" role={step === "form" ? undefined : "menuitem"} onClick={() => setStep(to)} className={`${ITEM} mb-1 text-gray-500`}>
      <Icon d="M15 18l-6-6 6-6" />
      <span>Retour</span>
    </button>
  );

  const header = (
    <div className="border-b border-gray-100 px-2.5 pb-2 pt-1">
      <p className="truncate text-xs font-semibold text-gray-900" title={company}>{company}</p>
      {center && <p className="truncate text-[11px] text-gray-500">{center}</p>}
    </div>
  );

  return createPortal(
    <div
      ref={menuRef}
      onKeyDown={onKeyDown}
      onContextMenu={(event) => event.preventDefault()}
      role={step === "form" ? "dialog" : "menu"}
      aria-label={step === "form" ? "Assigner une tâche" : `Actions sur ${company}`}
      className={`task-menu-in fixed z-[1000] rounded-lg border border-gray-200 bg-white p-1 shadow-xl ring-1 ring-black/5 ${step === "form" ? "w-80" : "w-64"}`}
      // Mesure avant affichage : opacité nulle (et non visibility:hidden) pour que le focus initial fonctionne.
      style={{ left: position?.left ?? x, top: position?.top ?? y, opacity: position ? undefined : 0 }}
    >
      {header}
      {step === "root" && (
        <div className="pt-1">
          <button type="button" role="menuitem" disabled={busy} onClick={() => void togglePriority()} className={`${ITEM} disabled:opacity-60`}>
            <span className={caseData.is_priority ? "text-gray-500" : "text-red-600"}>
              <Icon d={caseData.is_priority ? "M5 21V4h11l-2 4 2 4H5M3 3l18 18" : "M5 21V4h11l-2 4 2 4H5"} />
            </span>
            <span className="flex-1">{caseData.is_priority ? "Retirer la priorité" : "Marquer prioritaire"}</span>
          </button>
          <button type="button" role="menuitem" aria-haspopup="menu" data-submenu="true" onClick={() => setStep("types")} className={ITEM}>
            <span className="text-orange-600"><Icon d="M9 11l3 3 8-8M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></span>
            <span className="flex-1">Assigner une tâche</span>
            <Icon d="M9 6l6 6-6 6" className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>
      )}

      {step === "types" && (
        <div className="pt-1">
          {back("root")}
          <p className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Type de tâche</p>
          {CASE_TASK_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              role="menuitem"
              onClick={() => { setTaskType(type); setStep("form"); }}
              className={ITEM}
            >
              <span className="text-gray-500"><Icon d={TASK_ICONS[type]} /></span>
              <span>{CASE_TASK_LABELS[type]}</span>
            </button>
          ))}
        </div>
      )}

      {step === "form" && taskType && (
        <form
          className="space-y-3 px-1.5 pb-1.5 pt-1"
          onSubmit={(event) => { event.preventDefault(); void submit(); }}
          aria-labelledby={`${formId}-title`}
        >
          {back("types")}
          <div className="flex items-center gap-2 px-1">
            <span className="text-orange-600"><Icon d={TASK_ICONS[taskType]} /></span>
            <p id={`${formId}-title`} className="text-sm font-semibold text-gray-900">{CASE_TASK_LABELS[taskType]}</p>
          </div>

          <fieldset className="space-y-1.5 px-1">
            <legend className="mb-1 text-xs font-medium text-gray-600">Pour</legend>
            <label className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-sm ${teamPossible ? "cursor-pointer" : "cursor-not-allowed opacity-60"} ${target === "team" ? "border-indigo-300 bg-indigo-50" : "border-gray-200"}`}>
              <input
                type="radio"
                name={`${formId}-target`}
                className="mt-0.5 accent-indigo-600"
                checked={target === "team"}
                disabled={!teamPossible}
                onChange={() => setTarget("team")}
              />
              <span className="min-w-0">
                <span className="block font-medium text-gray-900">{center ? `Équipe ${center}` : "Équipe du centre"}</span>
                {teamPossible ? (
                  <span className="block truncate text-xs text-gray-500" title={recipients.map((m) => firstNameOf(m.profile)).join(", ")}>
                    {recipients.map((m) => firstNameOf(m.profile)).join(", ")}
                  </span>
                ) : (
                  <span className="block text-xs text-gray-500">
                    {center
                      ? "Aucun commercial dans cette équipe : seule l'option « Moi » est possible. Ajoutez des commerciaux à ce centre dans l'onglet Manager."
                      : "Ce dossier n'a pas de centre : seule l'option « Moi » est possible. Renseignez le centre dans la fiche pour l'assigner à une équipe."}
                  </span>
                )}
              </span>
            </label>
            <label className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-2 text-sm ${target === "self" ? "border-indigo-300 bg-indigo-50" : "border-gray-200"}`}>
              <input
                type="radio"
                name={`${formId}-target`}
                className="accent-indigo-600"
                checked={target === "self"}
                onChange={() => setTarget("self")}
              />
              <span className="font-medium text-gray-900">Moi</span>
            </label>
          </fieldset>

          <label className="block px-1">
            <span className="mb-1 flex items-baseline justify-between text-xs font-medium text-gray-600">
              <span>Note <span className="font-normal text-gray-400">(facultatif)</span></span>
              <span className={`font-normal tabular-nums ${note.length > CASE_TASK_NOTE_MAX_LENGTH - 50 ? "text-amber-700" : "text-gray-400"}`}>
                {note.length}/{CASE_TASK_NOTE_MAX_LENGTH}
              </span>
            </span>
            <textarea
              value={note}
              maxLength={CASE_TASK_NOTE_MAX_LENGTH}
              onChange={(event) => setNote(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void submit(); }
              }}
              rows={3}
              placeholder="Ex. : le client attend un appel avant 17 h"
              className="block w-full resize-none rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
          </label>

          <div className="flex justify-end gap-2 px-1">
            <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
              Annuler
            </button>
            <button type="submit" disabled={busy} className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:opacity-60">
              {busy ? "Assignation…" : "Assigner"}
            </button>
          </div>
        </form>
      )}
    </div>,
    document.body,
  );
}
